import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { POPUP } from 'utils/constants/popup';
import { useChat } from 'chat/useChat';
import { NBA_CHAT_ROOM } from 'chat/constants';
import app from 'new-ui/app';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import { useMatchesFilter } from 'modules/matchesFilter/MatchesFilterContext';
import { TREATMENT_TYPE_VALUES as TREATMENT_VALUES } from 'modules/matchesFilter/constants';
import styles from './TreatmentTypeSelector.module.css';

const SELECTION_TYPES = {
  ALL: 'All',
  CLINICAL_TRIALS: 'Clinical trial',
  STANDARD_OF_CARE: 'Approved Treatmetns',
};

const options = [
  SELECTION_TYPES.ALL,
  SELECTION_TYPES.CLINICAL_TRIALS,
  SELECTION_TYPES.STANDARD_OF_CARE,
];

const TreatmentTypeSelector = ({ onSelectionChange, onCompleteAndAdvance = () => {} }) => {
  const { treatmentTypes: selectedTreatmentTypes, setTreatmentTypes } = useMatchesFilter();
  const { openChatPopup } = useChat(NBA_CHAT_ROOM);

  const getSelectedTypeFromState = () => {
    const hasTrials = selectedTreatmentTypes.includes(TREATMENT_VALUES.CLINICAL_TRIALS);
    const hasSoc = selectedTreatmentTypes.includes(TREATMENT_VALUES.STANDARD_OF_CARE);

    if (hasTrials && hasSoc) return SELECTION_TYPES.ALL;
    if (hasTrials) return SELECTION_TYPES.CLINICAL_TRIALS;
    if (hasSoc) return SELECTION_TYPES.STANDARD_OF_CARE;
    return SELECTION_TYPES.ALL; // default
  };

  const selectedType = getSelectedTypeFromState();

  const handleButtonClick = useCallback((type) => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.TREATMENT_TYPE_SELECTED,
      { type },
    );

    let valueToSend;
    switch (type) {
      case SELECTION_TYPES.CLINICAL_TRIALS:
        valueToSend = [TREATMENT_VALUES.CLINICAL_TRIALS];
        break;
      case SELECTION_TYPES.STANDARD_OF_CARE:
        valueToSend = [TREATMENT_VALUES.STANDARD_OF_CARE];
        break;
      case SELECTION_TYPES.ALL:
      default:
        valueToSend = [
          TREATMENT_VALUES.CLINICAL_TRIALS,
          TREATMENT_VALUES.STANDARD_OF_CARE,
        ];
        break;
    }

    setTreatmentTypes(valueToSend);

    if (onSelectionChange) {
      onSelectionChange(valueToSend);
    }

    onCompleteAndAdvance();
  }, [setTreatmentTypes, onSelectionChange, onCompleteAndAdvance]);

  const handleLearnMoreClick = () => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.CTA_CLICKED,
      { cta_name: 'learn_advantages_of_clinical_trials' },
    );

    const initialUserMessage = 'Can you tell me about the advantages of clinical trials?';
    openChatPopup({
      popupType: POPUP.ASSISTANCE_CHAT,
      popupData: { preventOverlayClose: true },
      data: { initialUserMessage },
    });
  };

  return (
    <div className={styles.selectorContainer}>
      <p className={styles.selectorSubtitle}>Select the type of treatment</p>

      <div className={styles.buttonGroup}>
        {options.map((option) => (
          <button
            key={option}
            type="button"
            className={cx(styles.typeButton, { [styles.activeButton]: selectedType === option })}
            onClick={() => handleButtonClick(option)}
          >
            {option}
          </button>
        ))}
      </div>

      <p className={styles.selectorDescription}>Find the right treatment path for you</p>
      <button
        type="button"
        onClick={handleLearnMoreClick}
        className={styles.selectorLink}
      >
        Learn the advantages of clinical trials
      </button>
    </div>
  );
};

TreatmentTypeSelector.propTypes = {
  onSelectionChange: PropTypes.func,
  onCompleteAndAdvance: PropTypes.func,
};

export default TreatmentTypeSelector;
